import { Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Anecdote } from "../types/models";
import {
  getTimeUntilExpiration,
  truncateContent,
} from "../utils/anecdoteUtils";
import { AnecdoteStatusBadge } from "./AnecdoteStatusBadge";

type AnecdoteCardProps = {
  anecdote: Anecdote;
  onClick?: () => void;
};

export const AnecdoteCard = ({ anecdote, onClick }: AnecdoteCardProps) => {
  const { t } = useTranslation();
  const hasContent =
    anecdote.status === "submitted" ||
    anecdote.status === "accepted" ||
    anecdote.status === "rejected";

  const isClickable = hasContent && onClick;

  return (
    <div
      className={`border border-gray-200 rounded-lg p-5 bg-white shadow-sm hover:shadow-md transition-all ${
        isClickable ? "cursor-pointer" : ""
      }`}
      onClick={isClickable ? onClick : undefined}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-2">
        <div className="flex-1">
          <p className="text-sm text-gray-500 mt-0.5">
            <span className="text-gray-700">
              {anecdote.friend_first_name} {anecdote.friend_last_name}
            </span>
            {/* N'affiche l'expiration que si l'anecdote n'a pas de contenu */}
            {!anecdote.content && (
              <>
                {" - "}
                {anecdote.expired
                  ? t("anecdotes.modal.detail.expired")
                  : t("anecdotes.modal.detail.expiresIn", {
                      time: getTimeUntilExpiration(anecdote.expires_at),
                    })}
              </>
            )}
          </p>
        </div>
        <AnecdoteStatusBadge status={anecdote.status} />
      </div>

      {/* Content preview if submitted/accepted/rejected */}
      {hasContent && anecdote.content && (
        <>
          <div className="mt-4">
            <p className="text-sm text-gray-700 whitespace-pre-line line-clamp-3">
              {truncateContent(anecdote.content, 3)}
            </p>
          </div>
          {onClick && (
            <button className="mt-3 flex items-center gap-1.5 text-sm p-2 border border-delicate-600 rounded-md text-delicate-600 hover:text-delicate-700 font-medium">
              <Eye size={16} />
              {t("anecdotes.card.preview")}
            </button>
          )}
        </>
      )}
    </div>
  );
};
